import { useInfiniteQuery } from '@tanstack/react-query';
import type { AgentApiProfitsIndex } from '@/types/serializers';
import { fetchJson } from '@/utils/apiFetch';

export type ProfitsSearchParams = {
  query?: string;
  status?: string;
  organization_user_id?: string;
  company_organization_user_id?: string;
  candidate_organization_user_id?: string;
  company_id?: string;
  team_id?: string;
  sort?: string;
  entry_date_range?: {
    from?: string;
    to?: string;
  };
  page?: number;
};

const buildQueryParams = (params?: ProfitsSearchParams): string => {
  if (!params) return '';
  const qs: string[] = [];

  if (params.query) qs.push(`query=${encodeURIComponent(params.query)}`);
  if (params.status) qs.push(`status=${encodeURIComponent(params.status)}`);
  if (params.organization_user_id)
    qs.push(`organization_user_id=${encodeURIComponent(params.organization_user_id)}`);
  if (params.company_organization_user_id)
    qs.push(
      `company_organization_user_id=${encodeURIComponent(params.company_organization_user_id)}`
    );
  if (params.candidate_organization_user_id)
    qs.push(
      `candidate_organization_user_id=${encodeURIComponent(params.candidate_organization_user_id)}`
    );
  if (params.company_id) qs.push(`company_id=${encodeURIComponent(params.company_id)}`);
  if (params.team_id) qs.push(`team_id=${encodeURIComponent(params.team_id)}`);
  if (params.sort) qs.push(`sort=${encodeURIComponent(params.sort)}`);
  if (params.entry_date_range?.from)
    qs.push(`entry_date_range[from]=${encodeURIComponent(params.entry_date_range.from)}`);
  if (params.entry_date_range?.to)
    qs.push(`entry_date_range[to]=${encodeURIComponent(params.entry_date_range.to)}`);
  if (params.page !== undefined) qs.push(`page=${params.page}`);

  return qs.length > 0 ? `?${qs.join('&')}` : '';
};

export default function useFetchProfits(params?: ProfitsSearchParams) {
  return useInfiniteQuery({
    queryKey: ['/agent/api/profits', JSON.stringify(params)],
    queryFn: async ({ pageParam = 1 }) => {
      const queryWithPage = params ? { ...params, page: pageParam } : { page: pageParam };
      const queryString = buildQueryParams(queryWithPage);
      const data = await fetchJson<AgentApiProfitsIndex>(`/agent/api/profits.json${queryString}`);
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.is_last_page) return undefined;
      return allPages.length + 1;
    },
    initialPageParam: 1,
  });
}
